import './search-input.scss';
import { autoinject, bindable, observable } from 'aurelia-framework';
import { I18N } from 'aurelia-i18n';

export type SearchResult<T = unknown> = {
  id: number | string;
  title: string;
  meta: string;
  extra?: string[];
  return?: T;
};

export type SelectedItemDetails = {
  id: number | string;
  title: string;
};

type SearchHandler = ({ term }: { term: string }) => Promise<SearchResult[]>;
type GetSelectedItemDetails = ({ id }: { id: number | string }) => Promise<SelectedItemDetails>;
type OnSelectedItem = ({ item }: { item: unknown }) => void;

export type SearchBoxModel = {
  dialogTitle: string;
  searchHandler: SearchHandler;
};

@autoinject()
export class SearchInput {
  protected bound = false;

  @bindable()
  public disabled = false;

  @bindable()
  public searchEmpty: boolean;

  @bindable()
  public name: string;

  @bindable()
  @observable
  public itemId: string | number;
  itemIdChanged(id: string | number) {
    if (!this.bound) return;
    void this.getInitialResult(id);
  }

  @bindable()
  public placeholder = 'general.search';

  @bindable()
  public autoFocus = false;

  @bindable()
  public onSelected: OnSelectedItem;

  @bindable()
  protected searchHandler: SearchHandler;

  @bindable()
  protected getSelectedItemDetails: GetSelectedItemDetails;

  @bindable()
  public clearable: boolean;

  @bindable()
  public clearOnSelect: boolean;

  protected results: SearchResult[] = [];

  protected isLoading = false;

  protected focusedSearchResultIndex = -1;

  protected selectedItem: SelectedItemDetails | null = null;

  @observable()
  protected value: number | string | null = null;

  @observable()
  protected searchString: string = '';

  @observable()
  protected focused = false;
  focusedChanged(nv: boolean) {
    if (nv) {
      this.searchBoxFocused(nv);
    }
  }

  resultDirection: '' | 'search-box--result__inverse' = '';

  private readonly MAX_SEARCH_RESULTS = 20;

  constructor(
    private el: Element,
    protected t: I18N
  ) {}

  attached() {
    if (!this.searchHandler) {
      throw new Error('Search input requires a search handler => search-handler.call="nameOfFunction(term)"');
    }

    if (!this.getSelectedItemDetails && !this.clearOnSelect) {
      throw new Error('Search input requires a get selected item details => get-selected-item-details.call="nameOfFunction(id)"');
    }

    if (!this.onSelected) {
      throw new Error('Search input requires an on selected handler => on-selected.call="nameOfFunction(item)"');
    }

    const input: HTMLInputElement | null = this.el.querySelector('input');
    if (input && this.autoFocus) {
      input.focus();
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    input?.addEventListener('focusout', this.onDeFocus.bind(this));

    if (this.itemId) {
      void this.getInitialResult(this.itemId);
    }

    this.bound = true;
  }

  protected onDeFocus() {
    setTimeout(() => {
      this.clearSearch();
    }, 200); // Delay to allow click on search result
  }

  private async getInitialResult(id: string | number) {
    try {
      if (this.getSelectedItemDetails) {
        const selectedItem = await this.getSelectedItemDetails({ id });
        console.log(`Selected ${selectedItem}`);

        this.selectedItem = selectedItem;
      }
    } catch {
      // this.errorService.handleError(error);
    }
  }

  getDistanceToBottom(element: HTMLElement): number {
    return document.documentElement.scrollHeight - (element.getBoundingClientRect().bottom + window.scrollY);
  }

  protected async searchStringChanged(term: string) {
    try {
      if ((!term && !this.searchEmpty) || !this.focused) {
        this.results = [];
        return;
      }

      this.isLoading = true;
      const result = (await this.searchHandler({ term })) || [];

      const toBottom = this.getDistanceToBottom(this.el as HTMLElement);
      if (toBottom < 400) {
        this.resultDirection = 'search-box--result__inverse';
      }

      setTimeout(() => {
        this.isLoading = false;
        this.results = result;
      }, 200);
    } catch {
      this.isLoading = false;
    }
  }

  protected blurSearch() {
    setTimeout(() => {
      // this.clearSearch();
    }, 200);
  }

  protected handleSearchResult(event: KeyboardEvent) {
    if (event.code === 'Escape' || event.key === 'Escape') {
      this.clearSearch();
      return true;
    }

    if (!this.results.length) return true;

    if (event.code === 'ArrowDown' && this.results.length > this.focusedSearchResultIndex + 1) {
      this.focusedSearchResultIndex++;
    }

    if (event.code === 'ArrowUp' && this.focusedSearchResultIndex > 0) {
      this.focusedSearchResultIndex--;
    }

    if (event.code === 'Enter') {
      const res = this.results[this.focusedSearchResultIndex];
      this.clearSearch();
      this.setSelectedItem(res);
    }

    return true;
  }

  protected search(event: KeyboardEvent) {
    if (event.code === 'ArrowDown' || event.code === 'ArrowUp' || event.code === 'Enter') {
      return true;
    }
  }

  protected setFocusedSearchResult(itemIndex: number) {
    this.focusedSearchResultIndex = itemIndex;
  }

  protected searchBoxFocused(focus: boolean) {
    if (this.searchEmpty && focus) {
      void this.searchStringChanged('');
    }
  }

  protected clearSearch() {
    this.searchString = '';
    this.results = [];
    this.focusedSearchResultIndex = -1;
    this.focused = false;
  }

  protected async valueChanged(id: number) {
    if (id && !this.clearOnSelect) {
      this.selectedItem = await this.getSelectedItemDetails({ id });
    } else {
      this.selectedItem = null;
    }
  }

  protected setSelectedItem(item: SearchResult) {
    this.value = item.id;
    this.clearSearch();
    this.onSelected({ item: item });
  }

  protected clearSelected() {
    this.selectedItem = null;
    this.value = null;
    this.setSelectedItem({ id: undefined, title: undefined, meta: undefined });
  }
}
